/**
 * UTM parameter extraction and first-touch attribution.
 * Stores first-touch UTM in a cookie (30-day expiry).
 */

const UTM_COOKIE = "yayy_utm";
const UTM_COOKIE_DAYS = 30;

export interface UtmParams {
	source: string;
	medium: string;
	campaign: string;
	term: string;
	content: string;
	referrer: string;
}

const EMPTY_UTM: UtmParams = {
	source: "",
	medium: "",
	campaign: "",
	term: "",
	content: "",
	referrer: "",
};

/**
 * Extract UTM parameters from the current URL.
 */
export function extractUTM(): UtmParams {
	const params = new URLSearchParams(window.location.search);
	return {
		source: params.get("utm_source") || "",
		medium: params.get("utm_medium") || "",
		campaign: params.get("utm_campaign") || "",
		term: params.get("utm_term") || "",
		content: params.get("utm_content") || "",
		referrer: document.referrer || "",
	};
}

/**
 * Check if a UTM params object has any non-empty values.
 */
function hasUTM(utm: UtmParams): boolean {
	return Boolean(
		utm.source || utm.medium || utm.campaign || utm.term || utm.content,
	);
}

/**
 * Save first-touch UTM to cookie (30-day expiry).
 */
export function saveFirstTouchUTM(utm: UtmParams): void {
	if (!hasUTM(utm)) return;

	const data = JSON.stringify(utm);
	const expires = new Date();
	expires.setDate(expires.getDate() + UTM_COOKIE_DAYS);
	document.cookie = `${UTM_COOKIE}=${encodeURIComponent(data)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
}

/**
 * Load first-touch UTM from cookie.
 */
export function getFirstTouchUTM(): UtmParams {
	const match = document.cookie
		.split("; ")
		.find((c) => c.startsWith(`${UTM_COOKIE}=`));
	if (!match) return EMPTY_UTM;

	try {
		const data = decodeURIComponent(match.split("=").slice(1).join("="));
		return JSON.parse(data) as UtmParams;
	} catch {
		return EMPTY_UTM;
	}
}

/**
 * Clear UTM cookie (called after attribution is saved server-side).
 */
export function clearUTM(): void {
	document.cookie = `${UTM_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

/**
 * Get the effective UTM params: prefer URL params (current touch),
 * fall back to first-touch from cookie.
 */
export function getEffectiveUTM(): UtmParams {
	const current = extractUTM();
	if (hasUTM(current)) {
		// Save as first-touch if we don't have one yet.
		const existing = getFirstTouchUTM();
		if (!hasUTM(existing)) {
			saveFirstTouchUTM(current);
		}
		return current;
	}
	// No URL UTM — use first-touch.
	return getFirstTouchUTM();
}
